<template>
  <div class="relative">
    <!-- Bell Icon Button -->
    <button
      @click="toggleDropdown"
      type="button"
      class="relative p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg transition"
      :aria-label="`Notifications. ${unreadCount} unread`"
    >
      <BellIcon class="h-6 w-6" />

      <!-- Unread Badge -->
      <span
        v-if="unreadCount > 0"
        class="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full min-w-[20px]"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>

    <!-- Dropdown Panel -->
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        v-click-away="closeDropdown"
        class="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 dark:ring-gray-700 z-50 overflow-hidden"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
            Notifications
          </h3>
          <button
            v-if="unreadCount > 0 && !isMarkingAllRead"
            @click="markAllAsRead"
            type="button"
            class="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition"
          >
            Mark all as read
          </button>
          <span
            v-if="isMarkingAllRead"
            class="text-sm text-gray-500 dark:text-gray-400"
          >
            Marking all...
          </span>
        </div>

        <!-- Loading State -->
        <div
          v-if="isLoading"
          class="flex items-center justify-center p-8"
        >
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>

        <!-- Notifications List -->
        <div v-else class="max-h-[32rem] overflow-y-auto">
          <!-- Empty State -->
          <div
            v-if="notifications.length === 0"
            class="p-8 text-center text-gray-500 dark:text-gray-400"
          >
            <BellSlashIcon class="h-12 w-12 mx-auto mb-2 text-gray-400 dark:text-gray-600" />
            <p class="text-sm font-medium">No notifications</p>
            <p class="text-xs mt-1">You're all caught up!</p>
          </div>

          <!-- Notification Items -->
          <div v-else>
            <div
              v-for="notification in notifications"
              :key="notification.id"
              @click="handleNotificationClick(notification)"
              :class="[
                'flex items-start p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition',
                !notification.read_at ? 'bg-blue-50 dark:bg-blue-900/20' : ''
              ]"
              role="button"
              tabindex="0"
              @keydown.enter="handleNotificationClick(notification)"
              @keydown.space.prevent="handleNotificationClick(notification)"
            >
              <!-- Icon -->
              <div class="flex-shrink-0 mr-3">
                <div
                  :class="[
                    'p-2 rounded-lg',
                    getNotificationIconBgColor(notification.data.category)
                  ]"
                >
                  <component
                    :is="getNotificationIcon(notification.data.category)"
                    :class="[
                      'h-5 w-5',
                      getNotificationIconColor(notification.data.category)
                    ]"
                  />
                </div>
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ notification.data.title }}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mt-0.5">
                  {{ notification.data.message }}
                </p>
                <div class="flex items-center mt-1 space-x-2">
                  <p class="text-xs text-gray-500 dark:text-gray-500">
                    {{ formatRelativeTime(notification.created_at) }}
                  </p>
                  <span
                    v-if="notification.data.priority === 'urgent'"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                  >
                    Urgent
                  </span>
                  <span
                    v-else-if="notification.data.priority === 'high'"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
                  >
                    High
                  </span>
                </div>
              </div>

              <!-- Unread Indicator -->
              <div v-if="!notification.read_at" class="flex-shrink-0 ml-2">
                <div class="h-2.5 w-2.5 bg-blue-600 dark:bg-blue-500 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-center">
          <Link
            href="/notifications"
            class="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition"
            @click="closeDropdown"
          >
            View all notifications
          </Link>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { router, Link } from '@inertiajs/vue3';
import {
  BellIcon,
  BellSlashIcon,
  TicketIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  AtSymbolIcon,
  BuildingOfficeIcon,
  CogIcon,
  ShieldExclamationIcon,
} from '@heroicons/vue/24/outline';
import axios from 'axios';

// State
const isOpen = ref(false);
const isLoading = ref(false);
const isMarkingAllRead = ref(false);
const notifications = ref([]);
const unreadCount = ref(0);
let refreshInterval = null;

// Lifecycle hooks
onMounted(() => {
  fetchNotifications();

  // Auto-refresh every 30 seconds
  refreshInterval = setInterval(() => {
    // Only refresh if dropdown is closed to avoid disrupting user
    if (!isOpen.value) {
      fetchNotifications(true); // Silent refresh
    }
  }, 30000);

  // Listen for page visibility changes
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

/**
 * Fetch notifications from API
 *
 * @param {boolean} silent - If true, don't show loading state
 */
const fetchNotifications = async (silent = false) => {
  try {
    if (!silent) {
      isLoading.value = true;
    }

    const response = await axios.get('/notifications/unread');

    notifications.value = response.data.recent_notifications || [];
    unreadCount.value = response.data.unread_count || 0;
  } catch (error) {
    console.error('Failed to fetch notifications:', error);

    // Don't show error for silent refreshes
    if (!silent) {
      // Could show a toast notification here
    }
  } finally {
    if (!silent) {
      isLoading.value = false;
    }
  }
};

/**
 * Toggle dropdown open/closed
 */
const toggleDropdown = () => {
  isOpen.value = !isOpen.value;

  // Fetch fresh data when opening
  if (isOpen.value) {
    fetchNotifications();
  }
};

/**
 * Close dropdown
 */
const closeDropdown = () => {
  isOpen.value = false;
};

/**
 * Handle notification click
 *
 * @param {Object} notification
 */
const handleNotificationClick = async (notification) => {
  try {
    // Mark as read if unread
    if (!notification.read_at) {
      await axios.post(`/notifications/${notification.id}/read`);

      // Update local state
      notification.read_at = new Date().toISOString();
      unreadCount.value = Math.max(0, unreadCount.value - 1);
    }

    // Close dropdown
    closeDropdown();

    // Navigate to action URL if exists
    if (notification.data.action_url) {
      router.visit(notification.data.action_url);
    }
  } catch (error) {
    console.error('Failed to mark notification as read:', error);
  }
};

/**
 * Mark all notifications as read
 */
const markAllAsRead = async () => {
  try {
    isMarkingAllRead.value = true;

    await axios.post('/notifications/mark-all-read');

    // Update local state
    unreadCount.value = 0;
    notifications.value = notifications.value.map(notification => ({
      ...notification,
      read_at: notification.read_at || new Date().toISOString(),
    }));
  } catch (error) {
    console.error('Failed to mark all as read:', error);
  } finally {
    isMarkingAllRead.value = false;
  }
};

/**
 * Handle page visibility change (refresh when tab becomes active)
 */
const handleVisibilityChange = () => {
  if (!document.hidden && !isOpen.value) {
    fetchNotifications(true);
  }
};

/**
 * Get notification icon component based on category
 *
 * @param {string} category
 * @returns {Component}
 */
const getNotificationIcon = (category) => {
  const icons = {
    tickets: TicketIcon,
    work_orders: WrenchScrewdriverIcon,
    incidents: ExclamationTriangleIcon,
    team: UserGroupIcon,
    mentions: AtSymbolIcon,
    company: BuildingOfficeIcon,
    system: ShieldExclamationIcon,
  };

  return icons[category] || BellIcon;
};

/**
 * Get notification icon color class based on category
 *
 * @param {string} category
 * @returns {string}
 */
const getNotificationIconColor = (category) => {
  const colors = {
    tickets: 'text-blue-600 dark:text-blue-400',
    work_orders: 'text-green-600 dark:text-green-400',
    incidents: 'text-red-600 dark:text-red-400',
    team: 'text-purple-600 dark:text-purple-400',
    mentions: 'text-yellow-600 dark:text-yellow-400',
    company: 'text-indigo-600 dark:text-indigo-400',
    system: 'text-gray-600 dark:text-gray-400',
  };

  return colors[category] || 'text-gray-600 dark:text-gray-400';
};

/**
 * Get notification icon background color class based on category
 *
 * @param {string} category
 * @returns {string}
 */
const getNotificationIconBgColor = (category) => {
  const colors = {
    tickets: 'bg-blue-100 dark:bg-blue-900/30',
    work_orders: 'bg-green-100 dark:bg-green-900/30',
    incidents: 'bg-red-100 dark:bg-red-900/30',
    team: 'bg-purple-100 dark:bg-purple-900/30',
    mentions: 'bg-yellow-100 dark:bg-yellow-900/30',
    company: 'bg-indigo-100 dark:bg-indigo-900/30',
    system: 'bg-gray-100 dark:bg-gray-700',
  };

  return colors[category] || 'bg-gray-100 dark:bg-gray-700';
};

/**
 * Format relative time (e.g., "2 minutes ago")
 *
 * @param {string} timestamp
 * @returns {string}
 */
const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
};
</script>

<style scoped>
/* Scrollbar styling */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background-color: #f3f4f6; /* gray-100 */
}

:global(.dark) .overflow-y-auto::-webkit-scrollbar-track {
  background-color: #1f2937; /* gray-800 */
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #d1d5db; /* gray-300 */
  border-radius: 9999px;
}

:global(.dark) .overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #4b5563; /* gray-600 */
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: #9ca3af; /* gray-400 */
}

:global(.dark) .overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: #6b7280; /* gray-500 */
}

/* Line clamp utility for text truncation */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
